import React, { useMemo, useState } from 'react';
import { ChevronDown, Loader2, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';

/**
 * A choice in an N-choice confirm dialog. `key` is the value handed back to
 * `SelectionAction.run` as `choiceKey`. Cancel-style choices (see
 * `ConfirmSpec.cancelKeys`) just close the dialog without running.
 */
export interface ConfirmChoice {
  key: string;
  label: string;
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost';
  disabled?: boolean;
}

/**
 * The shape an action's `confirm(items)` returns to request a confirmation
 * step before running. `choices` should always include at least one cancel
 * choice (whose key is in `cancelKeys`).
 */
export interface ConfirmSpec {
  title: string;
  body: React.ReactNode;
  choices: ConfirmChoice[];
  /** Choice keys that simply close the dialog without running. Default: ['cancel']. */
  cancelKeys?: string[];
}

/**
 * A bulk action offered to the user for the current selection. The action owns
 * its own visibility, dynamic label, disabled reason, optional confirm step,
 * and the actual work performed in `run`.
 */
export interface SelectionAction<T> {
  key: string;
  /** Static label, or a function of the current items for inline consequence (e.g. `() => \`Delete (${n} eligible)\``). */
  label: string | ((items: T[]) => string);
  /** Optional hover title for the menu item, e.g. the untruncated form of a shortened `label`. */
  labelTitle?: string | ((items: T[]) => string);
  icon?: LucideIcon;
  /** Tailwind class for the icon color, e.g. 'text-red-600'. */
  iconClassName?: string;
  destructive?: boolean;
  /** Hide the action entirely for this selection. Default: always visible. */
  isVisible?: (items: T[]) => boolean;
  /** Return a string reason to render the action disabled with that reason in a tooltip; false/undefined = enabled. */
  disabledReason?: (items: T[]) => string | undefined;
  /** Return a spec to show the N-choice dialog first; null/undefined = run immediately. */
  confirm?: (items: T[]) => ConfirmSpec | null;
  /** Perform the action. `choiceKey` is the chosen confirm choice (undefined when there was no confirm step). */
  run: (items: T[], choiceKey?: string) => Promise<void> | void;
}

export interface SelectionActionBarProps<T> {
  /** The resolved selected objects (not ids). The bar hides when empty. */
  items: T[];
  onClear: () => void;
  /** Manifest × and partial-retry deselect. */
  onRemoveItem: (item: T) => void;
  actions: SelectionAction<T>[];
  getItemKey: (item: T) => string;
  /** Manifest row text. */
  getItemLabel: (item: T) => string;
  /** Manifest grouping (e.g. by discipline / task type). */
  getItemGroup: (item: T) => { key: string; label: string };
  /**
   * A short line shown above the bar, for a state every user must see. A
   * disabled action's tooltip never shows on a touch device.
   */
  notice?: string | null;
}

const resolveLabel = <T,>(
  label: string | ((items: T[]) => string),
  items: T[]
): string => (typeof label === 'function' ? label(items) : label);

const DEFAULT_CANCEL_KEYS = ['cancel'];
const ACTION_HANDOFF_DELAY_MS = 100;

/**
 * A floating selection action bar: count + clear, a manifest popover listing the
 * selected items grouped for review/removal, an Actions dropdown, an N-choice
 * confirm dialog, and a double-submit guard. Generic over the row type so it can
 * be reused across selection surfaces (Episode Manager tasks, intakes, coding...).
 */
function SelectionActionBar<T>({
  items,
  onClear,
  onRemoveItem,
  actions,
  getItemKey,
  getItemLabel,
  getItemGroup,
  notice = null,
}: SelectionActionBarProps<T>) {
  // The action currently mid-confirm (dialog open). null = no dialog.
  const [confirmingKey, setConfirmingKey] = useState<string | null>(null);
  const [confirmSpec, setConfirmSpec] = useState<ConfirmSpec | null>(null);
  // The action whose run() promise is pending. Drives the double-submit guard.
  const [runningKey, setRunningKey] = useState<string | null>(null);

  const isBusy = runningKey !== null;

  const visibleActions = useMemo(
    () => actions.filter(action => action.isVisible?.(items) !== false),
    [actions, items]
  );

  // Group manifest rows by getItemGroup, preserving first-seen group order.
  const groups = useMemo(() => {
    const order: string[] = [];
    const byKey = new Map<string, { label: string; items: T[] }>();
    for (const item of items) {
      const group = getItemGroup(item);
      let entry = byKey.get(group.key);
      if (!entry) {
        entry = { label: group.label, items: [] };
        byKey.set(group.key, entry);
        order.push(group.key);
      }
      entry.items.push(item);
    }
    return order.map(key => {
      const entry = byKey.get(key)!;
      return { key, label: entry.label, items: entry.items };
    });
  }, [items, getItemGroup]);

  if (items.length === 0) {
    return null;
  }

  const runAction = async (action: SelectionAction<T>, choiceKey?: string) => {
    if (isBusy) return;
    setRunningKey(action.key);
    try {
      await action.run(items, choiceKey);
    } finally {
      setRunningKey(null);
    }
  };

  const closeConfirm = () => {
    setConfirmingKey(null);
    setConfirmSpec(null);
  };

  const scheduleAfterDropdownClose = (callback: () => void) => {
    // Defer handoff until the dropdown has finished closing and released Radix's
    // body pointer-events lock. Opening another dialog synchronously from a menu
    // item can leave the page unclickable after that dialog closes.
    setTimeout(callback, ACTION_HANDOFF_DELAY_MS);
  };

  const handleActionSelect = (action: SelectionAction<T>) => {
    if (isBusy) return;
    if (action.disabledReason?.(items)) return;
    const spec = action.confirm?.(items) ?? null;
    if (spec) {
      scheduleAfterDropdownClose(() => {
        setConfirmingKey(action.key);
        setConfirmSpec(spec);
      });
      return;
    }
    scheduleAfterDropdownClose(() => void runAction(action));
  };

  const handleChoice = async (choice: ConfirmChoice) => {
    if (isBusy || choice.disabled) return;
    const action = actions.find(a => a.key === confirmingKey);
    const cancelKeys = confirmSpec?.cancelKeys ?? DEFAULT_CANCEL_KEYS;
    if (!action || cancelKeys.includes(choice.key)) {
      closeConfirm();
      return;
    }
    closeConfirm();
    await runAction(action, choice.key);
  };

  return (
    <TooltipProvider>
      <div className='fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50'>
        {notice && (
          <p
            role='status'
            data-testid='selection-action-bar-notice'
            className='mx-auto mb-1 w-max max-w-[calc(100vw-2rem)] rounded-md bg-primary px-3 py-1 text-center text-xs text-primary-foreground shadow-lg'
          >
            {notice}
          </p>
        )}
        <div className='bg-primary text-primary-foreground px-2 py-2 rounded-lg shadow-lg flex items-center gap-2'>
          <div className='flex items-center gap-2 h-7 pl-1 bg-black/20 rounded border border-white/10'>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type='button'
                  className='flex items-center gap-1 px-2 h-full text-sm rounded hover:bg-black/20 transition-colors'
                  aria-label={`Review ${items.length} selected ${
                    items.length === 1 ? 'item' : 'items'
                  }`}
                >
                  <span>{items.length} selected</span>
                  <ChevronDown className='w-3.5 h-3.5' />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align='start'
                side='top'
                className='w-72 max-h-80 overflow-y-auto p-0'
              >
                <div className='py-1'>
                  {groups.map(group => (
                    <div key={group.key}>
                      <div className='px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                        {group.label}
                      </div>
                      {group.items.map(item => (
                        <div
                          key={getItemKey(item)}
                          className='flex items-center justify-between gap-2 px-3 py-1.5 text-sm hover:bg-accent'
                        >
                          <span className='truncate'>{getItemLabel(item)}</span>
                          <Button
                            type='button'
                            size='icon'
                            variant='ghost'
                            className='h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground'
                            onClick={() => onRemoveItem(item)}
                            aria-label={`Remove ${getItemLabel(item)} from selection`}
                          >
                            <X className='w-3.5 h-3.5' />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            <Button
              onClick={onClear}
              size='icon'
              variant='ghost'
              className='h-7 w-7 rounded-l-none hover:bg-black/20'
              aria-label='Clear selection'
            >
              <X className='w-3.5 h-3.5' />
            </Button>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='ghost'
                disabled={isBusy}
                aria-label='Actions'
                className='h-7 px-2 text-sm bg-black/20 border border-white/10 rounded hover:bg-black/30 hover:text-primary-foreground'
              >
                {isBusy ? (
                  <Loader2 className='w-3.5 h-3.5 animate-spin' />
                ) : (
                  <>
                    Actions
                    <ChevronDown className='w-3.5 h-3.5' />
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              {visibleActions.map(action => {
                const Icon = action.icon;
                const reason = action.disabledReason?.(items);
                const disabled = Boolean(reason) || isBusy;
                const isRunning = runningKey === action.key;
                const itemNode = (
                  <DropdownMenuItem
                    key={action.key}
                    disabled={disabled}
                    title={
                      action.labelTitle
                        ? resolveLabel(action.labelTitle, items) || undefined
                        : undefined
                    }
                    className={
                      action.destructive
                        ? 'text-destructive focus:text-destructive'
                        : undefined
                    }
                    onSelect={event => {
                      // Keep the dropdown's close-then-open dance intact and
                      // never run while disabled or busy.
                      if (disabled) {
                        event.preventDefault();
                        return;
                      }
                      handleActionSelect(action);
                    }}
                  >
                    {isRunning ? (
                      <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                    ) : (
                      Icon && (
                        <Icon
                          className={`w-4 h-4 mr-2 ${action.iconClassName ?? ''}`}
                        />
                      )
                    )}
                    {resolveLabel(action.label, items)}
                  </DropdownMenuItem>
                );

                if (!reason) return itemNode;

                // Wrap a disabled item so the tooltip still surfaces on hover.
                return (
                  <Tooltip key={action.key}>
                    <TooltipTrigger asChild>
                      <div>{itemNode}</div>
                    </TooltipTrigger>
                    <TooltipContent side='left'>{reason}</TooltipContent>
                  </Tooltip>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <Dialog
        open={confirmingKey !== null}
        onOpenChange={open => {
          if (!open && !isBusy) closeConfirm();
        }}
      >
        <DialogContent
          onCloseAutoFocus={e => e.preventDefault()}
          onOpenAutoFocus={e => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{confirmSpec?.title}</DialogTitle>
          </DialogHeader>
          <DialogDescription asChild>
            <div className='py-2 text-sm text-muted-foreground'>
              {confirmSpec?.body}
            </div>
          </DialogDescription>
          <DialogFooter>
            {confirmSpec?.choices.map(choice => {
              const isCancel = (
                confirmSpec.cancelKeys ?? DEFAULT_CANCEL_KEYS
              ).includes(choice.key);
              const isRunningChoice =
                runningKey !== null &&
                runningKey === confirmingKey &&
                !isCancel;
              return (
                <Button
                  key={choice.key}
                  variant={choice.variant ?? (isCancel ? 'outline' : 'default')}
                  disabled={choice.disabled || (isBusy && !isCancel)}
                  onClick={() => void handleChoice(choice)}
                >
                  {isRunningChoice && (
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  )}
                  {choice.label}
                </Button>
              );
            })}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}

export default SelectionActionBar;
